import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: num1 + num2 });
    setCaptchaInput('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate captcha
    if (parseInt(captchaInput) !== captcha.answer) {
      toast.error('Incorrect captcha answer. Please try again.');
      generateCaptcha();
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
      generateCaptcha();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.location.href = '/'}>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-14" />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-home">Home</a>
              <a href="/catalog" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-catalog">Products</a>
              <a href="/about" className="text-gray-700 hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-about">About Us</a>
              <a href="/contact" className="text-[#0b1f3f] hover:text-[#fcce32] font-semibold transition-colors" data-testid="nav-contact">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Contact Content */}
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#0b1f3f] mb-4" data-testid="contact-heading">
              Get in Touch
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-[#0b1f3f] mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="h-12"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="h-12"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    required
                    rows={6}
                    className="resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Security Check (Anti-Spam) *
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="bg-gray-100 rounded-lg p-4 text-center mb-2">
                        <p className="text-lg font-bold text-[#0b1f3f]">
                          What is {captcha.num1} + {captcha.num2}?
                        </p>
                      </div>
                      <Input
                        type="number"
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                        placeholder="Enter answer"
                        required
                        className="h-12"
                        data-testid="captcha-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={generateCaptcha}
                      variant="outline"
                      className="mt-8"
                      data-testid="refresh-captcha-btn"
                    >
                      🔄 New
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#fcce32] text-[#0b1f3f] hover:bg-[#e0b82a] font-semibold text-lg"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-[#0b1f3f] mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#fcce32] bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#fcce32]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Address</h3>
                      <p className="text-gray-600">
                        82, Chauhan Market Rd, C Block<br />
                        Sector 53, Noida, Uttar Pradesh 201307
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#fcce32] bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#fcce32]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Phone</h3>
                      <p className="text-gray-600">085888 24881</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#fcce32] bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#fcce32]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                      <p className="text-gray-600">info@saifoodhouse.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-[#0b1f3f] mb-6">Business Hours</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Monday - Saturday</span>
                    <span className="text-gray-600">9:00 AM - 8:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Sunday</span>
                    <span className="text-gray-600">10:00 AM - 6:00 PM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0b1f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/saifoodhouse-logo.png" alt="Sai Food House" className="h-12 mb-4" />
              <p className="text-gray-300">
                Your trusted distributor of quality food & beverage products across India.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Contact Info</h4>
              <p className="text-gray-300 mb-2">82, Chauhan Market Rd, C Block</p>
              <p className="text-gray-300 mb-2">Sector 53, Noida, UP 201307</p>
              <p className="text-gray-300">Phone: 085888 24881</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[#fcce32]">Quick Links</h4>
              <div className="space-y-2">
                <a href="/catalog" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Products</a>
                <a href="/about" className="block text-gray-300 hover:text-[#fcce32] transition-colors">About Us</a>
                <a href="/contact" className="block text-gray-300 hover:text-[#fcce32] transition-colors">Contact Us</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>© 2025 Sai Food House. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
